import React, { useState, useEffect, useContext } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import Paper from '@material-ui/core/Paper';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import CssBaseline from '@material-ui/core/CssBaseline';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import { makeStyles } from '@material-ui/core/styles';
import GenericDialogBox from '../../components/GenericDialogBox';
import InfoBox from '../../components/InfoBox';
import DataService from "../../services/data.service";
import { useTranslation } from "react-i18next";
import PermissionContext from '../../contexts/PermissionContext';
import PRConceptList from '../../components/PRType/PrConceptList';

const validationSchema = yup.object().shape({
    code: yup
        .string('Enter code')
        .required('Code is required')
        .min(4, 'Codes should be of minimum 4 characters length'),
    name: yup
        .string('Enter category name')
        .required('Name is required')
        .min(4, 'Names should be of minimum 4 characters length'),
    period: yup
        .number('Enter days number to pay')
        .required('Days are required'),
});


const useStyles = makeStyles((theme) => ({
    paper: {
        marginTop: theme.spacing(18),
        maxWidth: 800,
        margin: 'auto',
        overflow: 'hidden',
        backgroundColor: 'white'
    },
    avatar: {
        margin: theme.spacing(1),
        backgroundColor: theme.palette.secondary.main,
    },
    form: {

        margin: theme.spacing(3, 2, 2),
    },
    submit: {
        margin: theme.spacing(3, 0, 2),
    },
}));



const endpoint = `pr-type`
const conceptEndpoint = `pr-concept`


const PRTypeForm = React.forwardRef((props, ref) => {
    const { t } = useTranslation();
    const { idToUpdate, permission } = props;

    const { userHasPermission } = useContext(PermissionContext)

    // console.log("UHP",userHasPermission)

    let hasPerm = userHasPermission(permission, 'can_list')
    let hasAddPerm = userHasPermission(permission, 'can_post')
    let hasEditPerm = userHasPermission(permission, 'can_patch')
    let hasGetPerm = userHasPermission(permission, 'can_get')
    let hasDeletePerm = userHasPermission(permission, 'can_delete')
    // for detail form
    let hasDeleteDetailPerm = userHasPermission(`${permission}-detail`, 'can_delete')



    // INFO NOTIFICATION VARS
    const [infoOpen, setInfoOpen] = useState(false);
    const [infoTitle, setInfoTitle] = useState('');
    const [infoBody, setInfoBody] = useState('');
    //***********************

    //Dialog Vars
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogueTitle, setDialogueTitle] = useState('');
    const [dialogueBody, setDialogueBody] = useState('');
    //**********

    // DIALOG FUNCTIONS
    const handleDialogOk = () => {
        setDialogOpen(false)
    };

    const handleDialogClose = () => {
        setDialogOpen(false)
    };

    const openDialogBox = (title, body) => {
        setDialogueTitle(title)
        setDialogueBody(body)
        setDialogOpen(true)
    };
    //*******************

    // Notification INFO functions
    const openNoticeBox = (title, body) => {
        setInfoTitle(title)
        setInfoBody(body)
        setInfoOpen(true)
    };

    const handleInfoClose = () => {
        setInfoOpen(false)
    };
    // *****************
    // Get concept data
    const [conceptChecked, setConceptChecked] = React.useState([]);
    const [conceptData, setConceptData] = React.useState([]);

    useEffect(() => {

        retrieveConceptData()
    }, []); // Those ARE connectec


    const retrieveConceptData = () => {
        DataService.getAll(conceptEndpoint)
            .then(response => {
                setConceptData(response.data)
                // console.log(response.data);
            })
            .catch(e => {
                openNoticeBox("Error", `Code: ${e.response.status} Message: ${e.response.statusText}`)
            });
    }
    const conceptIsChecked = (concept_id) => {
        return conceptChecked.filter(concept => concept.id === concept_id).length > 0;
    }

    const handleConceptToggle = (id, code, name) => () => {

        const alreadyExists = conceptIsChecked(id);
        const newChecked = [...conceptChecked];
        let value = {
            "id": id,
            "code": code,
            "name": name
        }

        const currentIndex = conceptChecked.indexOf(value);
        // console.log("data", id, code, name, currentIndex)
        if (alreadyExists) {
            newChecked.splice(currentIndex, 1);
        } else {
            newChecked.push(value);

        }

        setConceptChecked(newChecked);
        // console.log("concep", newChecked, conceptChecked, typeof conceptChecked)
    };
    //


    const emptyData = {
        'name': '',
        'code': '',
    }

    const [data, setData] = useState(emptyData);
    useEffect(() => {
        let id = typeof idToUpdate === 'object' ? 0 : idToUpdate
        getData(id)
    }, []);

    const getData = (id) => {
        DataService.get(id, endpoint)
            .then(response => {
                setData(response.data)
                setConceptChecked(response.data.pr_type_detail.map(detail => {
                    return {
                        "id": detail.concept.id,
                        "code": detail.concept.code,
                        "name": detail.concept.name
                    }
                }
                ))
            })
            .catch(e => {
                if (e.response.status != '404') {
                    openNoticeBox("Error", `Code: ${e.response.status} Message: ${e.response.statusText}`)
                    // setData(emptyData)
                } else {
                    setData(emptyData)
                }

            });
    }

    const updateData = (values) => {
        DataService.update(endpoint, values)
            .then(response => {
                openNoticeBox("Notice", t("record_updated_successfully", { "table": t("menu_pr_type") }))
                setData(emptyData)
            })
            .catch(e => {
                openNoticeBox("Error", `Code: ${e.response.status} Message: ${e.response.statusText}`)
            });
    }

    const addData = (values) => {
        DataService.create(endpoint, values)
            .then(response => {
                openNoticeBox("Notice", t("record_created_successfully", { "table": t("menu_pr_type") }))
                setData(emptyData)
            })
            .catch(e => {
                openNoticeBox("Error", `Code: ${e.response.status} Message: ${e.response.statusText}`)
            });
    }

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: data,
        validationSchema: validationSchema,
        onReset: () => {
        },
        onSubmit: (values, { setSubmitting, setErrors, setStatus, resetForm }) => {
            // console.log("cheked", conceptChecked, typeof conceptChecked, conceptChecked.length)
            let tmpValues = { ...values }
            let concept_ids = conceptChecked.map(concept => { return concept.id })
            tmpValues.concepts = concept_ids
            if (conceptChecked.length === 0) {
                openNoticeBox("Error", "Por favor seleccione al menos un concepto")
                return false
            }
            if (typeof idToUpdate === 'object') {
                addData(tmpValues)
                resetForm()
            }
            if (typeof idToUpdate === 'number') {
                // console.log("submit update",values)
                tmpValues.id = idToUpdate
                updateData(tmpValues)
            }

        },
    });
    const classes = useStyles();
    return (
        <Paper className={classes.paper}>
            <CssBaseline />
            <GenericDialogBox
                opn={dialogOpen}
                handleDialogOk={handleDialogOk}
                handleDialogClose={handleDialogClose}
                title={dialogueTitle}
                body={dialogueBody} />
            <InfoBox
                opn={infoOpen}
                handleDialogClose={handleInfoClose}
                title={infoTitle}
                body={infoBody} />

            <form className={classes.form} onSubmit={formik.handleSubmit} onReset={formik.handleReset}>
                <Grid container spacing={1}>
                    <Grid item xs={4}>
                        <TextField
                            variant="outlined"
                            margin="normal"
                            fullWidth
                            id="code"
                            label="Codigo"
                            name="code"
                            value={formik.values.code}
                            onChange={formik.handleChange}
                            error={formik.touched.code && Boolean(formik.errors.code)}
                            helperText={formik.touched.code && formik.errors.code}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextField
                            variant="outlined"
                            margin="normal"
                            fullWidth
                            id="name"
                            label="Nombre"
                            name="name"
                            value={formik.values.name}
                            onChange={formik.handleChange}
                            error={formik.touched.name && Boolean(formik.errors.name)}
                            helperText={formik.touched.name && formik.errors.name}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextField
                            variant="outlined"
                            margin="normal"
                            fullWidth
                            id="period"
                            label="Dias Nomina"
                            name="period"
                            value={formik.values.period}
                            onChange={formik.handleChange}
                            error={formik.touched.period && Boolean(formik.errors.period)}
                            helperText={formik.touched.period && formik.errors.period}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <PRConceptList
                            conceptChecked={conceptChecked}
                            setConceptChecked={setConceptChecked}
                            handleConceptToggle={handleConceptToggle}
                            conceptData={conceptData}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            color="primary"
                            className={classes.submit}
                            disabled={typeof idToUpdate === 'object' && hasAddPerm ? false : hasEditPerm ? false : true}
                        >
                            {typeof idToUpdate === 'object' ? t("save_form_button") : t("update_form_button")}
                        </Button>
                    </Grid>

                </Grid>
            </form>

            <Box mt={8}>

            </Box>
        </Paper>
    )
})

export default PRTypeForm;
